import { createContext, useContext, useState, useRef, useEffect, ReactNode } from "react";
import { NewsArticle } from "@/types/news";
import { toast } from "sonner";

interface ArticleAudio {
  id: string;
  title: string;
  source: string;
  audioUrl: string;
  imageUrl?: string;
}

interface ArticleAudioPlayerContextType {
  currentArticle: ArticleAudio | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  playbackRate: number;
  playArticle: (article: ArticleAudio) => void;
  pause: () => void;
  resume: () => void;
  seek: (time: number) => void;
  setVolume: (volume: number) => void;
  setPlaybackRate: (rate: number) => void;
  stopPlayback: () => void;
}

const ArticleAudioPlayerContext = createContext<ArticleAudioPlayerContextType | undefined>(undefined);

export const useArticleAudioPlayer = () => {
  const context = useContext(ArticleAudioPlayerContext);
  if (!context) {
    throw new Error("useArticleAudioPlayer must be used within ArticleAudioPlayerProvider");
  }
  return context;
};

export const ArticleAudioPlayerProvider = ({ children }: { children: ReactNode }) => {
  const [currentArticle, setCurrentArticle] = useState<ArticleAudio | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(1);
  const [playbackRate, setPlaybackRateState] = useState(1);
  
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Initialize audio element
  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.crossOrigin = "anonymous";
      audioRef.current.volume = volume;
      audioRef.current.playbackRate = playbackRate;

      const audio = audioRef.current;

      const handleTimeUpdate = () => {
        setCurrentTime(audio.currentTime);
      };

      const handleLoadedMetadata = () => {
        setDuration(audio.duration);
      };

      const handleEnded = () => {
        setIsPlaying(false);
        setCurrentTime(0);
      };

      const handleError = (e: Event) => {
        console.error("Audio playback error:", e);
        setIsPlaying(false);
        toast.error("Audio playback failed");
      };

      audio.addEventListener("timeupdate", handleTimeUpdate);
      audio.addEventListener("loadedmetadata", handleLoadedMetadata);
      audio.addEventListener("ended", handleEnded);
      audio.addEventListener("error", handleError);

      return () => {
        audio.removeEventListener("timeupdate", handleTimeUpdate);
        audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
        audio.removeEventListener("ended", handleEnded);
        audio.removeEventListener("error", handleError);
      };
    }
  }, []);

  // Sync volume changes to audio element
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  // Sync playback rate changes to audio element
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  const playArticle = async (article: ArticleAudio) => {
    if (!audioRef.current) return;

    try {
      // If same article and currently playing, do nothing
      if (currentArticle?.id === article.id && isPlaying) {
        return;
      }

      // If different article, load new audio
      if (currentArticle?.id !== article.id) {
        audioRef.current.src = article.audioUrl;
        setCurrentArticle(article);
        setCurrentTime(0);
      }

      await audioRef.current.play();
      setIsPlaying(true);
    } catch (error) {
      console.error("Failed to play article audio:", error);
      toast.error("Failed to play audio");
      setIsPlaying(false);
    }
  };

  const pause = () => {
    if (audioRef.current && isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const resume = async () => {
    if (audioRef.current && !isPlaying) {
      try {
        await audioRef.current.play();
        setIsPlaying(true);
      } catch (error) {
        console.error("Failed to resume audio:", error);
        toast.error("Failed to resume audio");
      }
    }
  };

  const seek = (time: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const setVolume = (newVolume: number) => {
    setVolumeState(Math.max(0, Math.min(1, newVolume)));
  };

  const setPlaybackRate = (rate: number) => {
    setPlaybackRateState(rate);
  };

  const stopPlayback = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsPlaying(false);
    setCurrentTime(0);
    setCurrentArticle(null);
  };

  return (
    <ArticleAudioPlayerContext.Provider
      value={{
        currentArticle,
        isPlaying,
        currentTime,
        duration,
        volume,
        playbackRate,
        playArticle,
        pause,
        resume,
        seek,
        setVolume,
        setPlaybackRate,
        stopPlayback,
      }}
    >
      {children}
    </ArticleAudioPlayerContext.Provider>
  );
};
